export type VehicleStatus = 'available' | 'reserved' | 'out'

export interface Reservation {
  id: string; vehicleId: string; projectId: string | null; projectName: string | null
  kolonaId: string | null; kolonaName: string | null; startsOn: string; endsOn: string
  purpose: string | null; status: 'reserved' | 'cancelled'; createdByName: string | null; createdAt: string
}

export interface Trip {
  id: string; vehicleId: string; reservationId: string | null; projectId: string | null; projectName: string | null
  kolonaId: string | null; kolonaName: string | null; driverName: string
  outAt: string; outOdometer: number; outFuel: number; outNote: string | null; outByName: string | null
  inAt: string | null; inOdometer: number | null; inFuel: number | null; inNote: string | null; inDamage: boolean; inByName: string | null
  km: number | null; outPhotos: string[]; inPhotos: string[]
}

export interface Vehicle {
  id: string; plate: string; brand: string | null; model: string | null; vehicleType: string | null; vin: string | null
  tuvDue: string | null; financing: string | null; monthlyPayment: number | null; hasNfc: boolean
  status: VehicleStatus; out: Trip | null; current: Reservation | null; next: Reservation | null; lastOdometer: number | null
}

export interface VehicleDetail { vehicle: Vehicle; reservations: Reservation[]; trips: Trip[] }
export interface FleetBoard { from: string; to: string; reservations: Reservation[]; trips: Trip[] }
export interface ProjectChoice { projectId: string; name: string | null; codeInternal: string | null }
export interface CrewChoice { kolonaId: string; name: string }

export const todayIso = () => new Date().toISOString().slice(0, 10)
export const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10)
}
export const vehicleName = (v: Pick<Vehicle, 'brand' | 'model'>) => [v.brand, v.model].filter(Boolean).join(' ')

/** GPS tracker name (same as AU), else register brand/model, else the plate. */
export function mapVehicleName(v: Pick<MapVehicle, 'name' | 'trackerName' | 'brand' | 'model' | 'plate'>): string {
  const gps = v.trackerName?.trim()
  if (gps) return gps
  const labeled = v.name?.trim()
  if (labeled && labeled !== v.plate) return labeled
  const reg = [v.brand, v.model].filter(Boolean).join(' ').trim()
  return reg || labeled || v.plate
}

/** A camera photo → JPEG data URL, at most 1600 px on the long edge (the server re-encodes anyway). */
export async function shrinkPhoto(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale)
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/jpeg', 0.82)
}

// ---- map (docs/fleet-design.md §Map) ----------------------------------------------------------------
export type PositionSource = 'tracker' | 'checkout' | 'returned' | 'site' | 'au'
export type SiteSource = 'manual' | 'rtk' | 'geocoded' | 'unfound' | 'none' | 'yard'
export interface GeoFix { lat: number; lng: number; accuracyM: number | null }
export interface MapPosition { lat: number; lng: number; source: PositionSource; at: string | null; accuracyM: number | null; siteSource: SiteSource | null }
export interface MapVehicle {
  id: string; plate: string; brand: string | null; model: string | null; status: VehicleStatus
  driverName: string | null; kolonaName: string | null; projectId: string | null; projectName: string | null; position: MapPosition | null
  kind: string; speedKmh: number | null; trackerName: string | null; name: string | null
}
export interface MapSite {
  projectId: string; name: string | null; codeInternal: string | null; lat: number | null; lng: number | null
  source: SiteSource; query: string | null; place: string | null; vehicles: number
}
export interface FleetMap { vehicles: MapVehicle[]; sites: MapSite[]; trackerConnected: boolean; geocoderEnabled: boolean }
export interface LookupResult { placed: { projectId: string; name: string; place: string }[]; unfound: string[]; remaining: number }
export interface TrackPoint { at: string; lat: number; lng: number; source: PositionSource; accuracyM: number | null }
export interface HistoryTrack { vehicleId: string; plate: string; points: TrackPoint[] }
export interface HistoryDay { date: string; vehicles: number; points: number }
export interface FleetHistory { days: HistoryDay[]; tracks: HistoryTrack[] }
